import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <img src="/konnect-logo.png" alt="Konnect" className="h-8 w-auto object-contain" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-medium hover:text-text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-20 sm:pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-gray" data-testid="privacy-policy-page">
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-text-primary mb-2 sm:mb-3">Privacy Policy</h1>
          <p className="text-gray-medium mb-8">Last Updated: April 1, 2026</p>

          <p className="text-text-primary leading-relaxed">
            This Privacy Policy describes how <strong>Kaharian Integrated Business Solutions</strong> ("Company," "we," "us," or "our"), 
            operating the Konnect platform ("Platform," "Service"), collects, uses, shares, and protects your personal information. 
            By accessing or using Konnect, you agree to the terms of this Privacy Policy.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">1. Information We Collect</h2>
          
          <h3 className="text-lg font-heading font-medium text-text-primary mt-6 mb-3">1.1 Information You Provide</h3>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li><strong>Account Information:</strong> Name, email address, phone number, business name, industry, and billing address when you register.</li>
            <li><strong>Payment Information:</strong> Credit card details and billing information processed securely through our payment processor, Stripe. We do not store full credit card numbers on our servers.</li>
            <li><strong>Business Data:</strong> Social media account connections, marketing content, email subscriber lists, website analytics data, and other marketing-related information you input into the Platform.</li>
            <li><strong>Communications:</strong> Messages, support requests, and feedback you send to us.</li>
          </ul>

          <h3 className="text-lg font-heading font-medium text-text-primary mt-6 mb-3">1.2 Information Collected Automatically</h3>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li><strong>Usage Data:</strong> Pages visited, features used, clicks, session duration, and interaction patterns within the Platform.</li>
            <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, IP address, and screen resolution.</li>
            <li><strong>Cookies & Tracking:</strong> We use cookies, local storage, and similar technologies for authentication, preferences, and analytics.</li>
          </ul>

          <h3 className="text-lg font-heading font-medium text-text-primary mt-6 mb-3">1.3 Third-Party Information</h3>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>Social media profile data when you connect accounts (Facebook, Instagram, LinkedIn, X/Twitter).</li>
            <li>Analytics data from connected services (Google Analytics, advertising platforms).</li>
            <li>Payment confirmation data from Stripe.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li><strong>Service Delivery:</strong> To provide, maintain, and improve the Konnect platform and its features.</li>
            <li><strong>AI Content Generation:</strong> Your inputs (topics, brand voice, preferences) are sent to AI providers (OpenAI) to generate marketing content. We do not use your proprietary content to train AI models.</li>
            <li><strong>Communication:</strong> To send service updates, billing notifications, security alerts, and support responses.</li>
            <li><strong>Analytics:</strong> To understand usage patterns and improve Platform performance and features.</li>
            <li><strong>Billing:</strong> To process payments, manage subscriptions, and prevent fraud.</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">3. Information Sharing</h2>
          <p className="text-text-primary leading-relaxed">We do not sell your personal information. We may share data with:</p>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li><strong>Service Providers:</strong> Stripe (payments), OpenAI (AI content generation), Resend (email delivery), and cloud infrastructure providers, strictly to operate the Platform.</li>
            <li><strong>Country Operators:</strong> If your account is managed by an authorized Konnect country operator or agency, they will have access to your account data within their operational territory.</li>
            <li><strong>Legal Requirements:</strong> When required by law, subpoena, or governmental request.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">4. Data Security</h2>
          <p className="text-text-primary leading-relaxed">
            We implement industry-standard security measures including encrypted data transmission (TLS/SSL), 
            hashed passwords (bcrypt), secure authentication tokens (JWT), and regular security audits. 
            However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">5. Data Retention</h2>
          <p className="text-text-primary leading-relaxed">
            We retain your data for as long as your account is active or as needed to provide services. 
            Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law 
            or for legitimate business purposes (e.g., billing records).
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">6. Your Rights</h2>
          <p className="text-text-primary leading-relaxed mb-3">Depending on your jurisdiction, you may have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten").</li>
            <li><strong>Portability:</strong> Request your data in a portable, machine-readable format.</li>
            <li><strong>Opt-Out:</strong> Opt out of marketing communications at any time.</li>
            <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances.</li>
          </ul>
          <p className="text-text-primary leading-relaxed mt-3">
            To exercise these rights, contact us at <a href="mailto:privacy@konnect.com" className="text-emerald-brand hover:underline">privacy@konnect.com</a>.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">7. International Data Transfers</h2>
          <p className="text-text-primary leading-relaxed">
            Konnect operates globally through authorized country operators. Your data may be processed in countries 
            other than your own. We ensure appropriate safeguards are in place, including data processing agreements 
            and compliance with applicable cross-border data transfer regulations (such as GDPR Standard Contractual Clauses).
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">8. Cookies Policy</h2>
          <p className="text-text-primary leading-relaxed">
            We use essential cookies for authentication and session management, and analytical cookies to understand Platform usage. 
            You can control cookie settings through your browser preferences. Disabling essential cookies may affect Platform functionality.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">9. Children's Privacy</h2>
          <p className="text-text-primary leading-relaxed">
            Konnect is not intended for use by individuals under the age of 18. We do not knowingly collect 
            personal information from children. If we become aware that we have collected data from a child, 
            we will promptly delete it.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">10. Changes to This Policy</h2>
          <p className="text-text-primary leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of material changes 
            via email or through a prominent notice on the Platform. Your continued use of Konnect after 
            changes constitutes acceptance of the updated policy.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">11. Contact Us</h2>
          <p className="text-text-primary leading-relaxed">
            If you have questions about this Privacy Policy or our data practices, contact us at:
          </p>
          <div className="bg-off-white rounded-xl p-6 mt-4">
            <p className="font-semibold text-text-primary">Kaharian Integrated Business Solutions</p>
            <p className="text-gray-medium mt-1">Email: <a href="mailto:privacy@konnect.com" className="text-emerald-brand hover:underline">privacy@konnect.com</a></p>
            <p className="text-gray-medium">Website: <a href="https://konnect.com" className="text-emerald-brand hover:underline">konnect.com</a></p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-dark border-t border-gray-700">
        <div className="max-w-4xl mx-auto text-center text-xs sm:text-sm text-gray-medium">
          <p>&copy; 2026 Konnect by Kaharian Integrated Business Solutions. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
